'use client';

import { createElement, Fragment, useEffect, useRef, useCallback } from 'react';
import { explorerAnalytics } from '@/lib/analytics';
import { createRoot, Root } from 'react-dom/client';
import { autocomplete, AutocompleteApi } from '@algolia/autocomplete-js';
import Fuse from 'fuse.js';
import { SearchItem } from '@/lib/search-data';
import { useUnsavedChanges } from '@/context/unsavedChanges';
import '@algolia/autocomplete-theme-classic';
import './search.css';

export function Search({ searchData }: { searchData: SearchItem[] }) {
  const searchRef = useRef<HTMLDivElement>(null);
  const fuseRef = useRef<Fuse<SearchItem> | null>(null);
  const autocompleteRef = useRef<AutocompleteApi<SearchItem> | null>(null);
  const panelRootRef = useRef<Root | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const lastQueryRef = useRef<string>('');
  const lastResultsCountRef = useRef<number>(0);

  const { confirmNavigation, hasUnsavedData } = useUnsavedChanges();
  const confirmNavigationRef = useRef(confirmNavigation);
  const hasUnsavedDataRef = useRef(hasUnsavedData);

  useEffect(() => {
    confirmNavigationRef.current = confirmNavigation;
    hasUnsavedDataRef.current = hasUnsavedData;
  }, [confirmNavigation, hasUnsavedData]);

  const handleNavigate = useCallback((url: string) => {
    if (hasUnsavedDataRef.current) {
      // Keep search panel open while showing the unsaved changes dialog
      autocompleteRef.current?.setIsOpen(true);
    }
    confirmNavigationRef.current(url);
  }, []);

  useEffect(() => {
    if (!searchRef.current || searchData.length === 0) return;

    if (document.querySelector('#api-search')?.childElementCount !== 0) {
      return;
    }

    fuseRef.current = new Fuse(searchData, {
      keys: ['title', 'url', 'description', 'resourceName'],
      minMatchCharLength: 2,
    });

    const search = (query: string) => {
      if (!query) return [];
      const results = fuseRef.current?.search(query) || [];
      if (results.length === 0) return [];
      return results.slice(0, 9).map((result) => result.item);
    };

    const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);
    const shortcut = isMac ? '⌘K' : 'Ctrl+K';
    const placeholder = `Search Endpoint... (${shortcut})`;

    autocompleteRef.current = autocomplete<SearchItem>({
      container: '#api-search',
      placeholder,
      defaultActiveItemId: 0,
      detachedMediaQuery: '',
      openOnFocus: true,
      renderer: { createElement, Fragment, render: () => {} },
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      },
      navigator: {
        navigate({ itemUrl, item }) {
          const items = fuseRef.current?.search(lastQueryRef.current) || [];
          const position = items.findIndex((r) => r.item.url === itemUrl) + 1;
          explorerAnalytics.searchPerformed({
            searchQuery: lastQueryRef.current,
            resultsCount: lastResultsCountRef.current,
            resultClicked: true,
            resultPosition: position,
            resultResource: (item as SearchItem)?.resourceName || '',
            resultEndpoint: (item as SearchItem)?.title || '',
          });
          handleNavigate(itemUrl);
        },
        navigateNewTab({ itemUrl, item }) {
          const items = fuseRef.current?.search(lastQueryRef.current) || [];
          const position = items.findIndex((r) => r.item.url === itemUrl) + 1;
          explorerAnalytics.searchPerformed({
            searchQuery: lastQueryRef.current,
            resultsCount: lastResultsCountRef.current,
            resultClicked: true,
            resultPosition: position,
            resultResource: (item as SearchItem)?.resourceName || '',
            resultEndpoint: (item as SearchItem)?.title || '',
          });
          const windowReference = window.open(itemUrl, '_blank', 'noopener');
          if (windowReference) {
            windowReference.focus();
          }
        },
        navigateNewWindow({ itemUrl }) {
          window.open(itemUrl, '_blank', 'noopener');
        },
      },
      getSources: ({ query }) => {
        if (!query) {
          return [];
        }
        return [
          {
            sourceId: 'api-explorer-goto',
            getItems({ query }) {
              const results = search(query);
              lastQueryRef.current = query;
              lastResultsCountRef.current = results.length;
              return results;
            },
            getItemUrl({ item }) {
              return item.url;
            },
            templates: {
              item({ item }) {
                return (
                  <a
                    href={item.url}
                    className="search-item"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const items =
                        fuseRef.current?.search(lastQueryRef.current) || [];
                      const position =
                        items.findIndex((r) => r.item.url === item.url) + 1;
                      explorerAnalytics.searchPerformed({
                        searchQuery: lastQueryRef.current,
                        resultsCount: lastResultsCountRef.current,
                        resultClicked: true,
                        resultPosition: position,
                        resultResource: item.resourceName || '',
                        resultEndpoint: item.title || '',
                      });
                      handleNavigate(item.url);
                    }}
                  >
                    <div className="search-item-header">
                      {item.method && (
                        <span
                          className={`search-method method-${item.method.toLowerCase()}`}
                        >
                          {item.method.toUpperCase()}
                        </span>
                      )}
                      <span className="search-title">{item.title}</span>
                    </div>
                    {item.description && (
                      <div className="search-description">
                        {item.description}
                      </div>
                    )}
                    {item.resourceName && (
                      <div className="search-resource">{item.resourceName}</div>
                    )}
                  </a>
                );
              },
              noResults() {
                return (
                  <div className="aa-NoResults">
                    No results for this query...
                  </div>
                );
              },
            },
          },
        ];
      },
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.querySelector('button')?.click();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      panelRootRef.current?.unmount();
      autocompleteRef.current?.destroy();
    };
  }, [searchData, handleNavigate]);

  return (
    <div id="api-search" ref={searchRef} className="api-explorer-search" />
  );
}
